// ตารางเวรพนักงาน (employee roster) helpers — the month grid + its shift drawer.
// Backend: the same Xano `employee_timetable` rows the ลงเวลา tab reads, proxied
// at /api/hr/schedule/employee*.
//
// Deliberately thin: shift-type pill tones, leave classification, the row's
// time accessors and the ค.ศ. date format all come from `clockin.js`, so a
// กะเช้า on this roster is the same colour and the same wording as the กะเช้า
// on the clock-in report. Only what is genuinely new to a CALENDAR view lives
// here: grid geometry, the shift window, and Xano's own calendar{} object.
import { isLeaveRow } from './clockin.js'

/* ── the month grid ──────────────────────────────────────────────────────
 * Monday-first: the clinic's week runs จันทร์→อาทิตย์, and a Sunday-first grid
 * splits the weekend across two rows, which is exactly the pair of days a
 * roster is read for. */
export const WEEKDAYS_TH = ['จ', 'อ', 'พ', 'พฤ', 'ศ', 'ส', 'อา']
const WEEKDAYS_FULL_TH = ['อาทิตย์', 'จันทร์', 'อังคาร', 'พุธ', 'พฤหัสบดี', 'ศุกร์', 'เสาร์']
const MONTHS_FULL_TH = [
  'มกราคม', 'กุมภาพันธ์', 'มีนาคม', 'เมษายน', 'พฤษภาคม', 'มิถุนายน',
  'กรกฎาคม', 'สิงหาคม', 'กันยายน', 'ตุลาคม', 'พฤศจิกายน', 'ธันวาคม',
]

const pad = (n) => String(n).padStart(2, '0')
export const isoOf = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
export const todayISO = () => isoOf(new Date())

// 42 cells (6 weeks) so the grid never changes height as the month steps —
// a roster you scan every day should not reflow under the cursor.
const GRID_CELLS = 42

export function monthGrid(year, month) {
  const first = new Date(year, month - 1, 1)
  const lead = (first.getDay() + 6) % 7 // getDay() is Sunday-first; shift to Monday
  const start = new Date(year, month - 1, 1 - lead)
  const out = []
  for (let i = 0; i < GRID_CELLS; i++) {
    const d = new Date(start.getFullYear(), start.getMonth(), start.getDate() + i)
    out.push({ date: isoOf(d), day: d.getDate(), inMonth: d.getMonth() === month - 1, weekend: d.getDay() === 0 || d.getDay() === 6 })
  }
  return out
}

// The GET window must cover the WHOLE grid, not the calendar month: the leading
// and trailing cells are real days and a shift on one of them belongs in its cell.
export function gridRange(year, month) {
  const cells = monthGrid(year, month)
  return { start_date: cells[0].date, end_date: cells[cells.length - 1].date }
}

// "เสาร์ 9 สิงหาคม 2569" — the day panel's header. Buddhist year, like the rest
// of the app's prose dates (the grid cells themselves are bare numbers).
export function fmtDayTH(iso) {
  const d = parseISODate(iso)
  if (!d) return '—'
  return `${WEEKDAYS_FULL_TH[d.getDay()]} ${d.getDate()} ${MONTHS_FULL_TH[d.getMonth()]} ${d.getFullYear() + 543}`
}

export function parseISODate(iso) {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(iso || ''))
  return m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : null
}

/* ── the shift window ────────────────────────────────────────────────────
 * `date` is the calendar day; `start_date`/`end_date` are epoch-ms timestamps
 * carrying the clock face. The clinic and the browser are both Asia/Bangkok, so
 * local getters are the conversion — no offset arithmetic. */
export function atTime(iso, hhmm) {
  const d = parseISODate(iso)
  const m = /^(\d{1,2}):(\d{2})$/.exec(String(hhmm || '').trim())
  if (!d || !m) return null
  d.setHours(Number(m[1]), Number(m[2]), 0, 0)
  return d.getTime()
}

// Typical windows per shift type, measured from the live roster. Only a
// STARTING POINT for a new row — every branch runs slightly different hours,
// which is what commonWindow() below corrects for.
export const DEFAULT_WINDOW = {
  'กะเช้า': { start: '09:30', end: '18:30' },
  'กะบ่าย': { start: '11:30', end: '20:30' },
  'กะควบ': { start: '09:30', end: '20:00' },
  'เวรผู้ช่วย': { start: '10:15', end: '20:30' },
  'WFH': { start: '09:00', end: '18:00' },
  'Office': { start: '09:00', end: '18:00' },
}
const FALLBACK_WINDOW = { start: '09:30', end: '18:30' }

const hhmm = (ts) => {
  const n = Number(ts)
  if (!Number.isFinite(n) || n <= 0) return ''
  const d = new Date(n)
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`
}

// The window a row actually holds, as <input type="time"> values.
export function windowOf(row) {
  return { start: hhmm(row?.start_date), end: hhmm(row?.end_date) }
}

/**
 * Opening times for a NEW row of `type` at `branchId`, learned from the rows
 * already on screen rather than hard-coded: เวรผู้ช่วย starts 10:15 at one
 * branch and 09:45 at another, so a single constant is wrong half the time.
 * The month's rows are already loaded, so this costs nothing.
 */
export function commonWindow(rows, type, branchId) {
  const tally = new Map()
  for (const r of Array.isArray(rows) ? rows : []) {
    if (String(r?.type || '') !== type) continue
    if (branchId && Number(r.branch_id) !== Number(branchId)) continue
    const w = windowOf(r)
    if (!w.start || !w.end) continue
    const k = `${w.start}|${w.end}`
    tally.set(k, (tally.get(k) || 0) + 1)
  }
  let best = null
  let bestN = 0
  for (const [k, n] of tally) if (n > bestN) { best = k; bestN = n }
  if (best) { const [start, end] = best.split('|'); return { start, end } }
  return DEFAULT_WINDOW[type] || FALLBACK_WINDOW
}

/* ── Xano's calendar{} object ────────────────────────────────────────────
 * The Bubble app renders this roster as a calendar and reads `calendar.color`,
 * so a row written here must carry the SAME hex the old app writes or the shift
 * turns up uncoloured over there. These are the values verbatim from live rows
 * — unrelated to the Tailwind pill tones the app itself draws with
 * (shiftTypeTone), which is why the two lists do not have to agree.
 * `title` and `unique_id` are built server-side; do not send them. */
export const CALENDAR_COLORS = {
  'กะเช้า': '#f6bd60',
  'กะบ่าย': '#f28482',
  'เวรผู้ช่วย': '#7fc8f8',
  'กะควบ': '#e0b1cb',
  'WFH': '#cfdbd5',
  'Office': '#e3f2fd',
  'ลากิจ': '#f4f3ee',
  'ลาป่วย': '#f2f2f2',
  // No live row to copy: the type is new (2026-10-31) and the Bubble app never
  // wrote it. stone-200, the tone the CRM paints it.
  'Leave without pay': '#e7e5e4',
}

export function calendarFor(type) {
  return { event_type: 'block', color: CALENDAR_COLORS[type] || '#e3f2fd', allday: false }
}

/* ── rows ────────────────────────────────────────────────────────────────── */

// Employee code is what this clinic's roster is keyed on — a readable Thai
// nickname+name ("นนนี่ อนัญญา"), not a serial. Prefer the row's own addon,
// fall back to the roster, then to the raw id so a chip is never blank.
export function codeOf(row, empById) {
  return row?.employee?.code || empById?.get(Number(row?.employee_id))?.code || `#${row?.employee_id ?? '—'}`
}

export function groupByDate(rows) {
  const m = new Map()
  for (const r of Array.isArray(rows) ? rows : []) {
    const k = String(r?.date || '').slice(0, 10)
    if (!k) continue
    if (!m.has(k)) m.set(k, [])
    m.get(k).push(r)
  }
  // th collation so codes sort the way a Thai reader expects them to; the id is
  // the tiebreaker, so a day whose rows arrive without the `employee` addon
  // still has a stable order instead of reshuffling on every refetch.
  for (const list of m.values()) {
    list.sort((a, b) => String(a?.employee?.code || '').localeCompare(String(b?.employee?.code || ''), 'th')
      || Number(a?.employee_id || 0) - Number(b?.employee_id || 0))
  }
  return m
}

// Legend counts — how much of each type the visible month holds.
export function typeCounts(rows) {
  const m = new Map()
  for (const r of Array.isArray(rows) ? rows : []) {
    const t = String(r?.type || '')
    m.set(t, (m.get(t) || 0) + 1)
  }
  return m
}

/**
 * The row that would collide with `draft` under Xano's one-active-row-per
 * (employee, date) rule, or null.
 *
 * Xano enforces that rule ONLY on create — its precondition is skipped whenever
 * an `id` is sent — so an edit that moves a shift onto an occupied day would
 * otherwise be accepted and quietly produce the duplicate the rule exists to
 * prevent. The proxy re-checks server-side; this copy is so the drawer can say
 * so before the user presses บันทึก.
 *
 * `rows` is only the loaded grid, so a date outside it returns null and the
 * server has the last word — hence `withinRange`.
 */
export function conflictIn(rows, draft) {
  const emp = Number(draft?.employee_id) || 0
  const date = String(draft?.date || '').slice(0, 10)
  if (!emp || !date) return null
  return (Array.isArray(rows) ? rows : []).find((r) => (
    Number(r.employee_id) === emp
    && String(r.date || '').slice(0, 10) === date
    && Number(r.id) !== Number(draft?.id || 0)
    && r.active !== false
  )) || null
}

export const withinRange = (iso, range) => !!iso && iso >= range.start_date && iso <= range.end_date

// A blank draft for the drawer. `active` is implicit on create; the editor never
// offers it as a control (deactivating is the ลบ action).
export function blankShift({ date, branchId, type = 'เวรผู้ช่วย', rows = [] }) {
  const w = commonWindow(rows, type, branchId)
  return {
    id: 0,
    employee_id: 0,
    branch_id: Number(branchId) || 0,
    type,
    date,
    start: w.start,
    end: w.end,
    travel_fee: false,
    travel_fee_amount: 0,
    remark: '',
  }
}

// Drawer state ← an existing row.
export function draftFromRow(row) {
  const w = windowOf(row)
  return {
    id: Number(row?.id) || 0,
    employee_id: Number(row?.employee_id) || 0,
    branch_id: Number(row?.branch_id) || 0,
    type: String(row?.type || ''),
    date: String(row?.date || '').slice(0, 10),
    start: w.start,
    end: w.end,
    travel_fee: row?.travel_fee === true,
    travel_fee_amount: Number(row?.travel_fee_amount || 0),
    remark: String(row?.remark || ''),
  }
}

/**
 * Drawer state → the wire payload.
 *
 * A leave day has no working window, so `start_date` and `end_date` both sit at
 * midnight of the day — the shape the existing ลากิจ/ลาป่วย rows already use.
 * Everything the ลงเวลา tab owns (clockin{}, allow_OT, special_OT, unnotice,
 * med_certificate) is absent on purpose: the proxy carries those forward from
 * the stored row so this drawer can never blank them.
 */
export function payloadFrom(draft) {
  const leave = isLeaveRow(draft)
  const start = leave ? atTime(draft.date, '00:00') : atTime(draft.date, draft.start)
  const end = leave ? start : atTime(draft.date, draft.end)
  return {
    employee_id: Number(draft.employee_id) || 0,
    branch_id: Number(draft.branch_id) || 0,
    type: draft.type,
    date: draft.date,
    start_date: start,
    end_date: end,
    travel_fee: !!draft.travel_fee,
    travel_fee_amount: draft.travel_fee ? Math.max(0, Math.round(Number(draft.travel_fee_amount) || 0)) : 0,
    remark: String(draft.remark || '').trim(),
    calendar: calendarFor(draft.type),
  }
}

/**
 * What is wrong with this draft, in Thai, or ''.
 * Leave rows skip the window checks — they have no window to check.
 */
export function validateDraft(draft) {
  if (!draft.employee_id) return 'กรุณาเลือกพนักงาน'
  if (!draft.branch_id) return 'กรุณาเลือกสาขา'
  if (!draft.type) return 'กรุณาเลือกประเภทเวร'
  if (!draft.date) return 'กรุณาเลือกวันที่'
  if (isLeaveRow(draft)) return ''
  if (!draft.start || !draft.end) return 'กรุณาระบุเวลาเข้า-ออกเวร'
  const a = atTime(draft.date, draft.start)
  const b = atTime(draft.date, draft.end)
  // Employee shifts do not cross midnight (the roster has none), so an
  // out-before-in is a typo, not an overnight — it stays an error.
  if (a == null || b == null || b <= a) return 'เวลาออกเวรต้องอยู่หลังเวลาเข้าเวร'
  if (draft.travel_fee && !(Number(draft.travel_fee_amount) > 0)) return 'กรุณาระบุจำนวนค่าเดินทาง'
  return ''
}

/**
 * The server's own sentence, dug back out of api.js's `HTTP 400: {json}` wrapper.
 * Xano's one-row-per-day precondition answers with a Thai `message` that tells
 * the user exactly what to do — swallowing it for a generic "บันทึกไม่สำเร็จ"
 * would be strictly worse than showing nothing.
 */
export function errText(e, fallback = 'บันทึกไม่สำเร็จ') {
  const msg = String(e?.message || '')
  const i = msg.indexOf('{')
  if (i >= 0) {
    try {
      const j = JSON.parse(msg.slice(i))
      const t = j?.message || j?.error || j?.detail
      if (t) return String(t)
    } catch { /* not json — fall through to the raw text */ }
  }
  return msg || fallback
}
